import { AxiosRequestConfig } from "axios"

import { privateFetch, publicFetch } from "../../libs/axios"
import {
  AddCustomerRequestType,
  AddCustomerResponseType,
  ApplyMembershipToCartRequestType,
  ApplyMembershipToCartResponseType,
  CustomerMembershipResponseType,
  DiscardCartResponseType,
  GetAllProcutAvailibleResponseType,
  GetAvailabilityForDateResponseType,
  GetAvailabilityForMonthResponseType,
  GetAvailabilityResponseType,
  GetCategoriesResponseType,
  GetCustomersResponseType,
  GetMembershipDiscountsResponseType,
  GetProductFieldsResponseType,
  IssueGiftcardForRefundRequestType,
  MembershipUsageResponseType,
} from "../../types/OrderTypes"
import { jsonToQueryString } from "../../utils/Common"

import {
  AddParticipantsToCartRequestType,
  AddParticipantsToCartResponseType,
  AddEditCartItemRequestType,
  AddEditCartItemResponseType,
  ApplyPromocodeRequestType,
  ApplyPromocodeResponseType,
  CreateCartRequestType,
  CreateCartResponseType,
  CreateEditExpenseDiscountRequestType,
  GetCartResponseType,
  GetLiveCapacityByProductIdPayload,
  GetLiveCapacityByProductIdResponseType,
  GetParticipantsFromCart,
  GetPerBookingFieldsOfCartResponseType,
  UpdateParticipantsInCartRequestType,
  UpdatePerBookingFieldsOfCartRequestType,
  UpdatePerBookingFieldsOfCartResponseType,
  UpdatePerOrderFieldsOfCartRequestType,
  UpdatePerOrderFieldsOfCartResponseType,
  ValidateGiftcardCodeResponseType,
  UpdateCustomerOfCartRequestType,
  AddEditAddonsRequestType,
  UpdateAffiliateOfCartRequestType,
} from "./types"

export const getCategoriesWithProducts = async (isIntegratedPage?: boolean) =>
  await privateFetch().get<GetCategoriesResponseType>(
    `/api/v1${isIntegratedPage ? "/public" : ""}/categories?include=products&filter[type]=product&paginate=false`
  )

export const getProductAvailability = async (
  productId: string,
  isIntegratedPage?: boolean | undefined
) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetCategoriesResponseType>(
      `/api/v1/public/product/${productId}/availability`
    )
  }
  return await privateFetch().get<GetAvailabilityResponseType>(
    `/api/v1/product/${productId}/availability`
  )
}

export const getActiveProducts = async (isIntegratedPage?: boolean, customerId = "") => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetCategoriesResponseType["data"]>(
      `/api/v1/public/categories?include=activeProducts&filter[type]=product&paginate=0&includeMembershipDetails=1&customer_id=${customerId}`
    )
  }
  return await privateFetch().get<GetCategoriesResponseType["data"]>(
    `/api/v1/categories?include=activeProducts&filter[type]=product&paginate=0&includeMembershipDetails=1&customer_id=${customerId}&getActiveProducts=true`
  )
}

export const getAllProductAvailibility = async (isIntegratedPage?: boolean | undefined) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetAllProcutAvailibleResponseType[]>(
      `/api/v1/public/products-availability/month?active_products=true`
    )
  }
  return await privateFetch().get<GetAllProcutAvailibleResponseType[]>(
    `/api/v1/products-availability/month?active_products=true`
  )
}

export const getProductAvailabilityForMonth = async (
  productId: string,
  date?: string,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetAvailabilityForMonthResponseType>(
      `/api/v1/public/product/${productId}/availability/calendar/${date ? `?date=${date}` : ""}`
    )
  }
  return await privateFetch().get<GetAvailabilityForMonthResponseType>(
    `/api/v1/product/${productId}/availability/calendar/${date ? `?date=${date}` : ""}`
  )
}

export const getProductAvailabilityForDate = async ({
  productId,
  date,
  isIntegratedPage = false,
}: {
  productId: string
  date: string
  isIntegratedPage?: boolean | undefined
}) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetAvailabilityForDateResponseType>(
      `/api/v1/public/product/${productId}/availability/check?date=${date}`
    )
  }
  return await privateFetch().get<GetAvailabilityForDateResponseType>(
    `/api/v1/product/${productId}/availability/check?date=${date}`
  )
}

export const getProductAvailabilityForDateWithCallToBook = async ({
  productId,
  date,
  isIntegratedPage = false,
}: {
  productId: string
  date: string
  isIntegratedPage?: boolean | undefined
}) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetAvailabilityForDateResponseType>(
      `/api/v1/public/product/${productId}/availability/check?date=${date}&ignore_call_to_book_closure=true`
    )
  }
  return await privateFetch().get<GetAvailabilityForDateResponseType>(
    `/api/v1/product/${productId}/availability/check?date=${date}&ignore_call_to_book_closure=true`
  )
}

export const getLiveCapacityByProductId = async (
  productId: string,
  body: GetLiveCapacityByProductIdPayload,
  isIntegratedPage: boolean,
  params = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<GetLiveCapacityByProductIdResponseType>(
      `/api/v1/public/product/${productId}/availability/live-capacity${jsonToQueryString(params)}`,
      body
    )
  }
  return await privateFetch().post<GetLiveCapacityByProductIdResponseType>(
    `/api/v1/product/${productId}/availability/live-capacity${jsonToQueryString(params)}`,
    body
  )
}

export const getLiveCapacityByProductIdForCallToBook = async (
  productId: string,
  body: GetLiveCapacityByProductIdPayload,
  isIntegratedPage: boolean,
  params = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<GetLiveCapacityByProductIdResponseType>(
      `/api/v1/public/product/${productId}/availability/live-capacity${jsonToQueryString(params)}`,
      body
    )
  }
  return await privateFetch().post<GetLiveCapacityByProductIdResponseType>(
    `/api/v1/product/${productId}/availability/live-capacity${jsonToQueryString(params)}`,
    body
  )
}

export const getAllCustomers = async (filter = {}) => {
  return await privateFetch().get<GetCustomersResponseType>(
    `/api/v1/customers/${jsonToQueryString(filter)}`
  )
}

export const addCustomer = async (body: AddCustomerRequestType, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().post<AddCustomerResponseType>(`/api/v1/public/customers`, body)
  }
  return await privateFetch().post<AddCustomerResponseType>(`/api/v1/customers`, body)
}

export const getProductFields = async (productId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetProductFieldsResponseType>(
      `/api/v1/public/product/${productId}/fields`
    )
  }
  return await privateFetch().get<GetProductFieldsResponseType>(
    `/api/v1/product/${productId}/fields`
  )
}

export const getUnitTypeLabel = async (productId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().get<any>(`/api/v1/public/product/${productId}/unit-type-labels`)
  }
  return await privateFetch().get<any>(`/api/v1/product/${productId}/unit-type-labels`)
}

export const createCart = async (body: CreateCartRequestType, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().post<CreateCartResponseType>(`/api/v1/public/cart`, body)
  }
  return await privateFetch().post<CreateCartResponseType>(`/api/v1/cart`, body)
}

export const updateCart = async (
  cartId: string,
  body: CreateCartRequestType,
  isIntegratedPage?: boolean,
  config: AxiosRequestConfig = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<CreateCartResponseType>(
      `/api/v1/public/cart/${cartId}`,
      body,
      config
    )
  }
  return await privateFetch().post<CreateCartResponseType>(`/api/v1/cart/${cartId}`, body, config)
}

export const deleteCartItem = async (cartId: string, cartItemId: string) =>
  await privateFetch().post<CreateCartResponseType>(`/api/v1/cart/${cartId}/delete-item`, {
    id: cartItemId,
  })

export const deleteCartItems = async (cartId: string, cartItemIds: string[]) =>
  await privateFetch().post<CreateCartResponseType>(`/api/v1/cart/${cartId}/delete-items`, {
    items: cartItemIds,
  })

export const getCart = async (cartId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetCartResponseType>(`/api/v1/public/cart/${cartId}`)
  }
  return await privateFetch().get<GetCartResponseType>(
    `/api/v1/cart/${cartId}?includeMembershipDetails=1`
  )
}

export const addParticipantsToCart = async (
  cartId: string,
  body: AddParticipantsToCartRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<AddParticipantsToCartResponseType>(
      `/api/v1/public/cart/${cartId}/update-participants`,
      body
    )
  }
  return await privateFetch().post<AddParticipantsToCartResponseType>(
    `/api/v1/cart/${cartId}/update-participants`,
    body
  )
}

export const getParticipantsFromCart = async (cartId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetParticipantsFromCart>(
      `/api/v1/public/cart/${cartId}/participants`
    )
  }
  return await privateFetch().get<GetParticipantsFromCart>(`/api/v1/cart/${cartId}/participants`)
}

export const updateParticipantsInCart = async (
  cartId: string,
  body: UpdateParticipantsInCartRequestType
) =>
  await privateFetch().post<AddParticipantsToCartResponseType>(
    `/api/v1/cart/${cartId}/participants`,
    body
  )

export const getPerBookingFieldsOfCart = async (cartId: string) =>
  await privateFetch().get<GetPerBookingFieldsOfCartResponseType>(
    `/api/v1/cart/${cartId}/booking-fields`
  )

export const updatePerBookingFieldsOfCart = async (
  { body, cartId, cartItemId }: UpdatePerBookingFieldsOfCartRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<UpdatePerBookingFieldsOfCartResponseType>(
      `/api/v1/public/cart/${cartId}/${cartItemId ? `${cartItemId}/` : ""}booking-fields`,
      body
    )
  }
  return await privateFetch().post<UpdatePerBookingFieldsOfCartResponseType>(
    `/api/v1/cart/${cartId}/${cartItemId ? `${cartItemId}/` : ""}booking-fields`,
    body
  )
}

export const getPerOrderFieldsOfCart = async (cartId: string) =>
  await privateFetch().get<GetParticipantsFromCart>(`/api/v1/cart/${cartId}/fields`)

export const updatePerOrderFieldsOfCart = async (
  cartId: string,
  body: UpdatePerOrderFieldsOfCartRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<UpdatePerOrderFieldsOfCartResponseType>(
      `/api/v1/public/cart/${cartId}/fields`,
      body
    )
  }
  return await privateFetch().post<UpdatePerOrderFieldsOfCartResponseType>(
    `/api/v1/cart/${cartId}/fields`,
    body
  )
}

export const applyPromocode = async (
  cartId: string,
  body: ApplyPromocodeRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<ApplyPromocodeResponseType>(
      `/api/v1/public/cart/${cartId}/apply-offer`,
      body
    )
  }
  return await privateFetch().post<ApplyPromocodeResponseType>(
    `/api/v1/cart/${cartId}/apply-offer`,
    body
  )
}

export const removePromocode = async (cartId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().post<CreateCartResponseType>(
      `/api/v1/public/cart/${cartId}/remove-offer`
    )
  }
  return await privateFetch().post<CreateCartResponseType>(`/api/v1/cart/${cartId}/remove-offer`)
}

export const discardCart = async (cartId: string) =>
  await privateFetch().delete<DiscardCartResponseType>(`/api/v1/cart/${cartId}`)

export const addGiftCardDetailsToCartItem = async (
  cartId: string,
  body: any,
  isIntegratedPage: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post(`/api/v1/public/cart/${cartId}/booking-fields`, body)
  }
  return await privateFetch().post(`/api/v1/cart/${cartId}/booking-fields`, body)
}

export const getDynamicRentalscheduling = async (
  productId: string,
  filter = {},
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().get(
      `/api/v1/public/product/${productId}/availability/rentals${jsonToQueryString(filter)}`
    )
  }
  return await privateFetch().get(
    `/api/v1/product/${productId}/availability/rentals${jsonToQueryString(filter)}`
  )
}

export const getDynamicRentalLiveCapacity = async (
  productId: string,
  body: any,
  isIntegratedPage: boolean,
  params = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().post(
      `/api/v1/public/product/${productId}/availability/live-capacity/range${jsonToQueryString(params)}`,
      body
    )
  }
  return await privateFetch().post(
    `/api/v1/product/${productId}/availability/live-capacity/range${jsonToQueryString(params)}`,
    body
  )
}

export const getCallToBookDynamicRentalLiveCapacity = async (
  productId: string,
  body: any,
  isIntegratedPage: boolean,
  params = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().post(
      `/api/v1/public/product/${productId}/availability/live-capacity/range${jsonToQueryString(params)}`,
      body
    )
  }
  return await privateFetch().post(
    `/api/v1/product/${productId}/availability/live-capacity/range${jsonToQueryString(params)}`,
    body
  )
}

export const validateGiftcardCode = async (
  cartId: string,
  body: ApplyPromocodeRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<ValidateGiftcardCodeResponseType>(
      `/api/v1/public/cart/${cartId}/validate-offer`,
      body
    )
  }
  return await privateFetch().post<ValidateGiftcardCodeResponseType>(
    `/api/v1/cart/${cartId}/validate-offer`,
    body
  )
}

export const createEditExpenseDiscount = async (
  cartId: string,
  body: CreateEditExpenseDiscountRequestType
) => await privateFetch().post<AddEditCartItemResponseType>(`/api/v1/cart/${cartId}/expense`, body)

export const getMembershipDiscounts = async (productId: string) =>
  await privateFetch().get<GetMembershipDiscountsResponseType>(
    `/api/v1/product/${productId}/membership/discount?paginate=0`
  )

export const applyMembershipToCart = async (
  cartId: string,
  body: ApplyMembershipToCartRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<ApplyMembershipToCartResponseType>(
      `/api/v1/public/cart/${cartId}/membership/apply`,
      body
    )
  }
  return await privateFetch().post<ApplyMembershipToCartResponseType>(
    `/api/v1/cart/${cartId}/membership/apply`,
    body
  )
}

export const removeMembershipFromCart = async (cartId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().post<CreateCartResponseType>(
      `/api/v1/public/cart/${cartId}/membership/remove`
    )
  }
  return await privateFetch().post<CreateCartResponseType>(
    `/api/v1/cart/${cartId}/membership/remove`
  )
}

export const getCustomerMembershipDetails = async (id: string) =>
  await privateFetch().get<CustomerMembershipResponseType>(`/api/v1/customer-membership/${id}`)

export const getMembershipUsage = async (id: string) =>
  await privateFetch().get<MembershipUsageResponseType>(`/api/v1/customer-membership/usage/${id}`)

export const issueGiftcardForRefund = async (
  orderId: string,
  body: IssueGiftcardForRefundRequestType
) => await privateFetch().post(`/api/v1/order/${orderId}/refund/as-gift-card`, body)

// Divided Cart APIs
export const initializeCart = async () =>
  await privateFetch().post<CreateCartResponseType>("/api/v1/cart/initialize")

export const addProductToCart = async (cartId: string, body: AddEditCartItemRequestType) =>
  await privateFetch().post<AddEditCartItemResponseType>(`/api/v1/cart/${cartId}/add-item`, body)

export const updateCartItem = async (
  cartId: string,
  cartItemId: string,
  body: AddEditCartItemRequestType
) =>
  await privateFetch().post<AddEditCartItemResponseType>(
    `/api/v1/cart/${cartId}/update-item/${cartItemId}`,
    body
  )

export const updateCustomerOfCart = async (cartId: string, body: UpdateCustomerOfCartRequestType) =>
  await privateFetch().post<AddEditCartItemResponseType>(
    `/api/v1/cart/${cartId}/update-customer`,
    body
  )

export const addEditAddonsOfCart = async (
  cartId: string,
  cartItemId: string,
  body: AddEditAddonsRequestType
) =>
  await privateFetch().post<AddEditCartItemResponseType>(
    `/api/v1/cart/${cartId}/multiple-add-on/${cartItemId}`,
    body
  )

export const cloneOrderToCart = async (orderId: string) =>
  await privateFetch().post<CreateCartResponseType>(`/api/v1/order/${orderId}/clone-for-edit`)

export const updateAffiliateOfCart = async (
  cartId: string,
  body: UpdateAffiliateOfCartRequestType
) =>
  await privateFetch().post<AddEditCartItemResponseType>(
    `/api/v1/cart/${cartId}/update-affiliate`,
    body
  )

export const removeAffiliateOfCart = async (cartId: string) =>
  await privateFetch().post<AddEditCartItemResponseType>(`/api/v1/cart/${cartId}/remove-affiliate`)
